/*
 * Copyright (c) 2026. Bolmate B.V.
 */
import './style/orderCard.scss';

import {capitalize, parseMoney} from '@dvrd/dvr-controls';

import classNames from 'classnames';

import React, {Fragment, useRef} from 'react';

import {useTranslation} from 'react-i18next';

import Invoice from '../../models/invoiceModel';

import {useInView} from '../misc/useInView';

import {getCountryCode} from '../../util/countryUtil';

import {getInvoiceStatusLabels, InvoiceStatusElement} from '../../util/dashboardUtil';

import {onCopy} from '../../util/utils';

import {BolmateImageFallback, BolmateMenu, BolmateMenuOption, BolmateTooltip} from '../controls';

import InfoIcon from '../icon/infoIcon';

import CopyIcon from '../misc/copyIcon';

import CountryFlag from '../misc/countryFlag';

interface Props {
    invoice: Invoice;
    fromActiveAccount: boolean;
    /** Platform label of the owning account when the invoice belongs to a non-selected account. */
    accountName: string | null;
    /** Built lazily on menu open — hundreds of closed card menus per list
     *  would otherwise allocate their option closures on every render. */
    options: BolmateMenuOption[] | (() => BolmateMenuOption[]);
    onClickAssignInvoiceNumber: (invoice: Invoice) => (evt?: KeyboardEvent | React.MouseEvent) => void;
    /** Called once when the card first scrolls into view, so the controller can
     *  lazily enrich only the cards the user actually sees (image, returns,
     *  profit) instead of the whole page up front. */
    onVisible?: (invoiceId: string) => void;
}

export default function OrderCard(props: Props) {
    const {invoice, fromActiveAccount, accountName, options, onClickAssignInvoiceNumber, onVisible} = props;
    const {t} = useTranslation();
    const cardRef = useRef<HTMLDivElement>(null);
    // Report visibility once per card instance; a list reload swaps in a fresh
    // Invoice (new resetKey) and re-arms, the enrichment merge keeps identity.
    useInView(cardRef, () => onVisible?.(invoice.id), {resetKey: invoice});
    // The first status element is the invoice's main status label (rendered as
    // the card's chip); the rest render as the compact status circles.
    const [chip, ...circles] = getInvoiceStatusLabels(invoice);
    const quantityBadge = getQuantityBadge();

    function renderStatusElement(element: InvoiceStatusElement, index: number) {
        let renderElement: React.ReactElement;
        if (element.type === 'invoice-num')
            renderElement = React.cloneElement<any>(element.element, {
                // 'clickable' adds the pointer cursor so this status circle reads
                // as interactive — it's the only status element wired with onClick.
                className: classNames(element.element.props.className, 'clickable'),
                onClick: onClickAssignInvoiceNumber(invoice)
            });
        else renderElement = element.element;
        return <Fragment key={index}>{renderElement}</Fragment>;
    }

    // The badge overlaid on the thumbnail encodes two different things — "+N" =
    // N more distinct products in the order, "xN" = N units of the single
    // pictured product — so each carries an explanatory tooltip (title) to make
    // the meaning unambiguous on hover.
    function getQuantityBadge(): {label: string; title: string} | null {
        if (invoice.productCount > 1)
            return {
                label: `+${invoice.productCount - 1}`,
                title: t('dashboard.quantityBadge.moreProducts', 'Deze order bevat {{count}} verschillende producten', {
                    count: invoice.productCount
                })
            };
        if (invoice.productQuantity > 1)
            return {
                label: `x${invoice.productQuantity}`,
                title: t('dashboard.quantityBadge.units', 'Deze order bevat {{count}} stuks van dit product', {
                    count: invoice.productQuantity
                })
            };
        return null;
    }

    function renderProfit() {
        const profit = invoice.profit;
        return (
            // Same hover label as the quantity badge (BolmateTooltip) instead of a
            // native title, so it reads consistently across the card.
            <BolmateTooltip text={t('dashboard.profitLabel', 'Winst')}>
                <label
                    className={classNames(
                        'cell-sub',
                        'profit-sub',
                        profit !== null && (profit < 0 ? 'negative' : 'positive')
                    )}
                >
                    {profit === null ? '--' : `€ ${parseMoney(profit)}`}
                </label>
            </BolmateTooltip>
        );
    }

    function renderOtherAccount() {
        return (
            <div className='other-account-container'>
                <InfoIcon className='other-account-icon' />
                <span className='other-account-message'>
                    {t('dashboard.otherAccount', 'Deze order komt uit niet-geselecteerd account')} {accountName}
                </span>
            </div>
        );
    }

    return (
        <div className='order-card' ref={cardRef}>
            <div className='card-image'>
                <BolmateImageFallback
                    src={invoice.productImageUrl}
                    imageClassName='product-image'
                    badge={quantityBadge?.label ?? null}
                    badgeTitle={quantityBadge?.title}
                />
            </div>
            <div className='card-cell customer-cell'>
                <div className='with-copy-action'>
                    <label className='cell-main'>
                        <CountryFlag countryCode={getCountryCode(invoice.country)} /> {invoice.fullName}
                    </label>
                    <CopyIcon onClick={onCopy(invoice.fullName, true)} className='copy-action' autoReset />
                </div>
                <label className='cell-sub'>
                    {/* PWA-05: order time moved here next to the account type ("05:48, Private")
                        — it used to sit as a third line in the amount block. */}
                    {invoice.momentDate.format('HH:mm')},{' '}
                    {t(
                        `dashboard.invoiceCustomer${capitalize(invoice.customerType.toLowerCase())}`,
                        invoice.translatedCustomerType
                    )}
                </label>
            </div>
            <div className='card-cell order-cell'>
                <div className='with-copy-action'>
                    {!fromActiveAccount && renderOtherAccount()}
                    {/* Hover label, mirroring the quantity badge's tooltip — the bare
                        numbers give no clue which is the order and which the invoice number. */}
                    <BolmateTooltip text={t('dashboard.orderNumberLabel', 'Ordernummer')}>
                        <label className='cell-main'>{invoice.orderId}</label>
                    </BolmateTooltip>
                    <CopyIcon onClick={onCopy(invoice.orderId, true)} className='copy-action' autoReset />
                </div>
                <div className='with-copy-action'>
                    <BolmateTooltip text={t('dashboard.invoiceNumberLabel', 'Factuurnummer')}>
                        <label className='cell-sub'>{invoice.invoiceNum || '-'}</label>
                    </BolmateTooltip>
                    {!!invoice.invoiceNum && (
                        <CopyIcon onClick={onCopy(invoice.invoiceNum, true)} className='copy-action' autoReset />
                    )}
                </div>
            </div>
            <div className='card-cell amount-cell'>
                <label className='cell-main'>&euro; {parseMoney(invoice.total)}</label>
                {renderProfit()}
            </div>
            <div className='card-statuses'>{circles.map(renderStatusElement)}</div>
            <div className='card-chip'>{renderStatusElement(chip, -1)}</div>
            <BolmateMenu options={options} align='right' className='card-options' />
        </div>
    );
}
